<template>
  <vs-popup
    class="department-log-popup"
    :title="computedTitle"
    :active.sync="open"
    @close="onClose"
    :buttons-hidden="true"
  >
    <vx-card no-shadow>
      <div id="department-log-table" class="vs-con-loading__container min-h-32">
        <vs-table
          :key="tableKey"
          class="my-4"
          hoverFlat
          stripe
          :data="logs"
          noDataText="No department logs found"
          :max-items="meta.per_page ? meta.per_page : 10"
        >
          <template slot="thead">
            <vs-th width="25%">DATE/TIME</vs-th>
            <vs-th width="35%">EVENT</vs-th>
            <vs-th width="40%">DONE BY</vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr v-for="(tr_item, indextr) in data" :key="indextr">
              <vs-td>
                {{ formatLogDate(tr_item.created_at) }}
              </vs-td>

              <vs-td>
                <vs-chip transparent :color="getEventColor(tr_item.event)">
                  {{ tr_item.event }}
                </vs-chip>
              </vs-td>

              <vs-td>
                {{ tr_item.causer ? tr_item.causer.name : 'System' }}
              </vs-td>

              <!-- Expand Slot: Field Changes Diff -->
              <template slot="expand">
                <div class="con-expand-users w-4/5 py-2">
                  <div v-if="tr_item.properties && buildDiffRows(tr_item.properties).length">
                    <vs-table noDataText="" class="w-full">
                      <template slot="thead">
                        <vs-th width="30%">CHANGED FIELD</vs-th>
                        <vs-th width="35%">PREVIOUS VALUE</vs-th>
                        <vs-th width="35%">NEW VALUE</vs-th>
                      </template>
                      <template>
                        <vs-tr
                          v-for="(row, i) in buildDiffRows(tr_item.properties)"
                          :key="i"
                        >
                          <vs-td class="font-semibold">{{ row.field }}</vs-td>
                          <vs-td class="text-danger">{{ row.old }}</vs-td>
                          <vs-td class="text-success">{{ row.new }}</vs-td>
                        </vs-tr>
                      </template>
                    </vs-table>
                  </div>
                  <p v-else class="text-grey text-sm p-4">
                    No field changes recorded for this event.
                  </p>
                </div>
              </template>
            </vs-tr>
          </template>
        </vs-table>

        <vs-pagination
          v-if="meta && meta.last_page"
          v-on:changeMaxItems="changePerPage"
          :size-array="meta.total ? meta.total : 20"
          :max-items="perPage"
          description-body="Rows per page"
          description-connector="of"
          description-title="Records"
          description
          :description-items="descriptionItems"
          :total="meta.last_page ? meta.last_page : 0"
          v-model="page"
          class="p-5"
        >
        </vs-pagination>
      </div>
    </vx-card>
  </vs-popup>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessages from '@/mixins/ErrorMessages'
import SuccessMessage from '@/mixins/SuccessMessage'
import { defaultPagination, perPageList } from '@/constants/config'

const DepartmentsRepository = RepositoryFactory.get('departments')

export default {
  name: 'ViewDepartmentLogPopUp',
  mixins: [ErrorMessages, SuccessMessage],
  data () {
    return {
      open: false,
      tableKey: 0,
      departmentId: null,
      departmentName: '',
      logs: [],
      loading: false,
      page: 1,
      perPage: defaultPagination,
      descriptionItems: perPageList,
      meta: {
        total: 0,
        last_page: 0,
        per_page: defaultPagination
      }
    }
  },
  computed: {
    computedTitle () {
      return this.departmentName ? `${this.departmentName} - ACTIVITY LOGS` : 'DEPARTMENT ACTIVITY LOGS'
    }
  },
  methods: {
    resetState () {
      this.logs = []
      this.meta = {
        total: 0,
        last_page: 0,
        per_page: this.perPage || defaultPagination
      }
      this.page = 1
      this.loading = false
      this.tableKey++
    },

    onClose () {
      this.open = false
      this.resetState()
    },

    async openPopup (departmentId, departmentName = '') {
      this.resetState()
      this.departmentId = departmentId
      this.departmentName = departmentName
      this.open = true
      await this.fetchLogs()
    },

    async fetchLogs () {
      if (!this.departmentId) return
      this.loading = true
      this.$vs.loading({
        container: '#department-log-table',
        scale: 0.45
      })
      try {
        const response = await DepartmentsRepository.getDepartmentLogs(
          this.departmentId,
          this.page,
          this.perPage
        )
        const result = response && response.data ? response.data : {}
        this.logs = Array.isArray(result.data) ? result.data : []
        if (result.meta) {
          this.meta = result.meta
        }
      } catch (e) {
        console.error('Error fetching department logs:', e)
        this.convertAndNotifyError(e)
      } finally {
        this.loading = false
        this.$vs.loading.close('#department-log-table > .con-vs-loading')
      }
    },

    changePerPage (index) {
      this.perPage = this.descriptionItems[index]
      this.page = 1
    },

    formatLogDate (dateStr) {
      if (!dateStr) return '—'
      if (typeof this.__dateTimeFormatter === 'function') {
        return this.__dateTimeFormatter(dateStr)
      }
      return dateStr
    },

    getEventColor (event) {
      if (!event) return 'primary'
      const e = event.toLowerCase()
      if (e.includes('create') || e.includes('add')) return 'success'
      if (e.includes('update') || e.includes('edit')) return 'warning'
      if (e.includes('delete') || e.includes('remove')) return 'danger'
      return 'primary'
    },

    /* =============================
       DIFF ENGINE
    ============================== */
    buildDiffRows (properties) {
      if (!properties || (!properties.old && !properties.new)) return []

      const oldFlat = this.flattenObject(properties.old || {})
      const newFlat = this.flattenObject(properties.new || {})

      const keys = new Set(
        Object.keys(oldFlat).concat(Object.keys(newFlat))
      )

      return Array.from(keys)
        .map(k => ({
          field: this.prettyPath(k),
          old: this.displayValue(oldFlat[k]),
          new: this.displayValue(newFlat[k])
        }))
        .filter(r => r.old !== r.new)
    },

    flattenObject (obj, parent = '', res = {}) {
      if (!obj) return res

      Object.keys(obj).forEach(key => {
        const value = obj[key]
        const path = parent ? `${parent}.${key}` : key

        if (Array.isArray(value)) {
          value.forEach((v, i) => {
            const label = (v && typeof v === 'object' && v.log_label) ? `(${v.log_label})` : `[${i}]`
            const nextPath = `${path}.${label}`
            if (typeof v === 'object' && v !== null) {
              this.flattenObject(v, nextPath, res)
            } else {
              res[nextPath] = v
            }
          })
        } else if (typeof value === 'object' && value !== null) {
          this.flattenObject(value, path, res)
        } else {
          res[path] = value
        }
      })

      return res
    },

    prettyPath (path) {
      return path
        .replace(/\.\(/g, ' → ')
        .replace(/\)\./g, ' → ')
        .replace(/\./g, ' → ')
        .replace(/[()]/g, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    },

    displayValue (val) {
      if (val === undefined || val === null || val === '') return '—'
      if (typeof val === 'object') return JSON.stringify(val)
      return String(val)
    }
  },
  watch: {
    open (val) {
      if (!val) {
        this.resetState()
      }
    },
    page () {
      if (this.open && this.departmentId) {
        this.fetchLogs()
      }
    },
    perPage () {
      if (this.open && this.departmentId) {
        this.fetchLogs()
      }
    }
  }
}
</script>

<style scoped>
.department-log-popup >>> .vs-popup {
  min-width: 70% !important;
}
.con-expand-users {
  padding: 10px;
}
</style>
